import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TerritoryContext = createContext(null);

export const useTerritory = () => {
  const context = useContext(TerritoryContext);
  if (!context) throw new Error('useTerritory must be used within TerritoryProvider');
  return context;
};

export const TerritoryProvider = ({ children }) => {
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [communes, setCommunes] = useState([]);
  
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedCommune, setSelectedCommune] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Use ref to track if initial load is complete
  const initialLoadDone = useRef(false);

  // Fetch countries on mount and initialize
  useEffect(() => {
    const initializeTerritory = async () => {
      try {
        // Fetch countries first
        const countryRes = await axios.get(`${API_URL}/api/territories/countries`);
        setCountries(countryRes.data);
        
        // Try to load saved selections
        let savedCountry = null;
        let savedRegion = null;
        let savedCommune = null;
        
        try {
          const savedCountryStr = localStorage.getItem('selectedCountry');
          const savedRegionStr = localStorage.getItem('selectedRegion');
          const savedCommuneStr = localStorage.getItem('selectedCommune');
          
          if (savedCountryStr) savedCountry = JSON.parse(savedCountryStr);
          if (savedRegionStr) savedRegion = JSON.parse(savedRegionStr);
          if (savedCommuneStr) savedCommune = JSON.parse(savedCommuneStr);
        } catch (e) {
          // Clear corrupted localStorage
          localStorage.removeItem('selectedCountry');
          localStorage.removeItem('selectedRegion');
          localStorage.removeItem('selectedCommune');
        }
        
        // Validate and set country
        const validCountry = savedCountry && countryRes.data.find(c => c.id === savedCountry.id);
        const countryToUse = validCountry ? savedCountry : (countryRes.data.find(c => c.code === 'CL') || countryRes.data[0]);
        
        if (countryToUse) {
          setSelectedCountry(countryToUse);
          
          // Fetch ALL regions for this country (not just those with events)
          const regionRes = await axios.get(`${API_URL}/api/territories/regions?country_id=${countryToUse.id}&has_events=true`);
          setRegions(regionRes.data);
          
          // First-visit geolocation: if the user has no saved region, try to
          // detect their region from their IP so the Landing loads events
          // relevant to where they actually are. Falls back silently.
          let geoRegion = null;
          let geoCommune = null;
          if (!savedRegion && countryToUse.code === 'CL') {
            try {
              const geoRes = await axios.get(`${API_URL}/api/territories/geolocate`, { timeout: 4000 });
              if (geoRes.data?.detected && geoRes.data?.region_id) {
                geoRegion = regionRes.data.find(r => r.id === geoRes.data.region_id) || null;
                if (geoRegion && geoRes.data.commune_id) {
                  // Commune resolution happens after we fetch communes below
                  geoCommune = { id: geoRes.data.commune_id, name: geoRes.data.commune_name };
                }
              }
            } catch (e) { /* geolocation is optional */ }
          }
          
          // Validate and set region — priority: saved > geolocated > Antofagasta default
          const validRegion = savedRegion && regionRes.data.find(r => r.id === savedRegion.id);
          const defaultRegion = countryToUse.code === 'CL'
            ? (regionRes.data.find(r => /antofagasta/i.test(r.name)) || regionRes.data[0])
            : regionRes.data[0];
          const regionToUse = validRegion ? savedRegion : (geoRegion || defaultRegion);
          
          if (regionToUse) {
            setSelectedRegion(regionToUse);
            // Persist the geo-detected region so we don't hit the geolocate API
            // on every reload (user can still override via the selector).
            if (!validRegion && geoRegion) {
              try { localStorage.setItem('selectedRegion', JSON.stringify(geoRegion)); } catch (e) { /* quota */ }
            }
            
            // Fetch ALL communes for this region
            const communeRes = await axios.get(`${API_URL}/api/territories/communes?region_id=${regionToUse.id}&has_events=true`);
            setCommunes(communeRes.data);
            
            // Validate and set commune - default to null (Todas las comunas)
            const validCommune = savedCommune && communeRes.data.find(c => c.id === savedCommune.id);
            if (validCommune) {
              setSelectedCommune(savedCommune);
            } else if (!savedCommune && geoCommune && communeRes.data.find(c => c.id === geoCommune.id)) {
              // Use IP-detected commune only if it actually exists in the region's active list
              const detectedCommune = communeRes.data.find(c => c.id === geoCommune.id);
              setSelectedCommune(detectedCommune);
              try { localStorage.setItem('selectedCommune', JSON.stringify(detectedCommune)); } catch (e) { /* quota */ }
            } else {
              // Default to null = "Todas las comunas"
              setSelectedCommune(null);
            }
          }
        }
        
        initialLoadDone.current = true;
      } catch (error) {
        console.error('Failed to initialize territory:', error);
      } finally {
        setLoading(false);
      }
    };
    
    initializeTerritory();
  }, []);

  const selectCountry = useCallback(async (country) => {
    if (!country || !initialLoadDone.current) return;
    
    setSelectedCountry(country);
    setSelectedRegion(null);
    setSelectedCommune(null);
    setCommunes([]);
    
    localStorage.setItem('selectedCountry', JSON.stringify(country));
    localStorage.removeItem('selectedRegion');
    localStorage.removeItem('selectedCommune');
    
    try {
      const response = await axios.get(`${API_URL}/api/territories/regions?country_id=${country.id}&has_events=true`);
      setRegions(response.data);
      
      if (response.data.length > 0) {
        const firstRegion = country.code === 'CL'
          ? (response.data.find(r => /antofagasta/i.test(r.name)) || response.data[0])
          : response.data[0];
        setSelectedRegion(firstRegion);
        localStorage.setItem('selectedRegion', JSON.stringify(firstRegion));
        
        // Fetch communes for first region
        const communeRes = await axios.get(`${API_URL}/api/territories/communes?region_id=${firstRegion.id}&has_events=true`);
        setCommunes(communeRes.data);
        // Default to null = "Todas las comunas"
        setSelectedCommune(null);
        localStorage.removeItem('selectedCommune');
      } else {
        setRegions([]);
      }
    } catch (error) {
      console.error('Failed to fetch regions:', error);
    }
  }, []);

  const selectRegion = useCallback(async (region) => {
    if (!region || !initialLoadDone.current) return;
    
    setSelectedRegion(region);
    setSelectedCommune(null);
    
    localStorage.setItem('selectedRegion', JSON.stringify(region));
    localStorage.removeItem('selectedCommune');
    
    try {
      const response = await axios.get(`${API_URL}/api/territories/communes?region_id=${region.id}&has_events=true`);
      setCommunes(response.data);
      
      // Default to null = "Todas las comunas"
      setSelectedCommune(null);
    } catch (error) {
      console.error('Failed to fetch communes:', error);
    }
  }, []);

  const selectCommune = useCallback((commune) => {
    if (!initialLoadDone.current) return;
    
    setSelectedCommune(commune);
    if (commune) {
      localStorage.setItem('selectedCommune', JSON.stringify(commune));
    } else {
      localStorage.removeItem('selectedCommune');
    }
  }, []);

  const clearTerritory = useCallback(() => {
    setSelectedCountry(null);
    setSelectedRegion(null);
    setSelectedCommune(null);
    setRegions([]);
    setCommunes([]);
    localStorage.removeItem('selectedCountry');
    localStorage.removeItem('selectedRegion');
    localStorage.removeItem('selectedCommune');
  }, []);

  return (
    <TerritoryContext.Provider value={{
      countries,
      regions,
      communes,
      selectedCountry,
      selectedRegion,
      selectedCommune,
      selectCountry,
      selectRegion,
      selectCommune,
      clearTerritory,
      loading,
      hasTerritory: !!selectedCommune
    }}>
      {children}
    </TerritoryContext.Provider>
  );
};
